import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { Euro, GraduationCap, Briefcase, TrendingUp, Award, CheckCircle2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STUDENTS_IMAGE = "https://images.unsplash.com/photo-1543269865-cbf427effbad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwc3R1ZGVudHMlMjB3b3JraW5nJTIwbGFwdG9wfGVufDB8fHx8MTc3NTc0Mzk5Mnww&ixlib=rb-4.1.0&q=85";

const benefits = [
  {
    icon: Euro,
    title: "Rémunération dès le 1er jour",
    description: "Salaire entre 775€ et 1 867€/mois selon l'âge (% du SMIC)",
    highlight: "775€ - 1 867€/mois"
  },
  {
    icon: GraduationCap,
    title: "Formation 100% prise en charge",
    description: "Coût de formation financé intégralement par l'OPCO de l'entreprise",
    highlight: "0€ de frais"
  },
  {
    icon: Briefcase,
    title: "Expérience professionnelle",
    description: "80% du temps en entreprise, opérationnel dès la 1ère semaine",
    highlight: "80% terrain"
  },
  {
    icon: TrendingUp,
    title: "Emploi à la clé",
    description: "Profil parmi les plus recrutés en France",
    highlight: ">75% insertion"
  },
  {
    icon: Award,
    title: "Diplôme reconnu",
    description: "Titre Bac+2 équivalent BTS, validable bloc par bloc",
    highlight: "RNCP Niveau 5"
  },
  {
    icon: ArrowRight,
    title: "Passerelle Bachelor",
    description: "Accès direct au Bachelor Bac+3 dès septembre 2027",
    highlight: "Bac+3"
  }
];

export const Students = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    birth_date: '',
    address: '',
    city: '',
    postal_code: '',
    education_level: '',
    motivation: '',
    preferred_start: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Scroll to candidature section if hash is present
    if (window.location.hash === '#candidature') {
      setTimeout(() => {
        document.getElementById('candidature')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/applications/student`, formData);
      toast.success('Candidature envoyée avec succès !', {
        description: 'Nous reviendrons vers vous dans les plus brefs délais.'
      });
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        birth_date: '',
        address: '',
        city: '',
        postal_code: '',
        education_level: '',
        motivation: '',
        preferred_start: ''
      });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer plus tard.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="students-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="bg-[#0B1F3A] py-24" data-testid="students-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
                Espace Étudiants
              </span>
              <h1 className="font-serif-heading text-4xl sm:text-5xl font-semibold text-white mb-6">
                Lancez votre carrière commerciale
              </h1>
              <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
              <p className="text-[#F5F5F5]/90 text-lg mb-8 leading-relaxed">
                Une formation en alternance 100% gratuite et rémunérée dès le premier jour. 
                Obtenez un diplôme reconnu par l'État et décrochez un emploi dans la vente B2B.
              </p>
              
              <a href="#candidature">
                <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 py-6" data-testid="students-hero-cta">
                  Candidater maintenant
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </a>
            </div>
            
            <div className="relative">
              <img 
                src={STUDENTS_IMAGE}
                alt="Étudiants Institut BIA"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Vos Avantages
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A]">
              Pourquoi choisir l'Institut BIA ?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="border border-[#E6E6E6] p-8 hover:border-[#C8A75A] transition-colors hover-lift"
                data-testid={`benefit-${index}`}
              >
                <benefit.icon className="text-[#C8A75A] mb-4" size={32} strokeWidth={1.5} />
                <div className="bg-[#0B1F3A] text-[#C8A75A] text-sm font-semibold px-3 py-1 inline-block mb-4">
                  {benefit.highlight}
                </div>
                <h3 className="font-serif-heading text-xl font-semibold text-[#0B1F3A] mb-2">
                  {benefit.title}
                </h3>
                <p className="text-[#4A4A4A] text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-24 bg-[#F5F5F5]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Processus
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A]">
              Comment candidater ?
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Candidature en ligne", desc: "Remplissez le formulaire ci-dessous" },
              { step: "02", title: "Étude du dossier", desc: "Nous analysons votre profil sous 15 jours" },
              { step: "03", title: "Entretien", desc: "Rencontre avec notre équipe pédagogique" },
              { step: "04", title: "Intégration", desc: "Bienvenue dans votre nouvelle formation !" }
            ].map((item, index) => (
              <div key={index} className="text-center" data-testid={`process-step-${index}`}>
                <div className="w-16 h-16 bg-[#0B1F3A] flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#C8A75A] font-serif-heading text-2xl font-semibold">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-semibold text-[#0B1F3A] mb-2">{item.title}</h3>
                <p className="text-[#4A4A4A] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="candidature" className="py-24 bg-white scroll-mt-20" data-testid="application-form-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Candidature
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A] mb-4">
              Déposez votre candidature
            </h2>
            <p className="text-[#4A4A4A]">
              Remplissez ce formulaire et nous vous recontacterons dans les plus brefs délais.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-[#F5F5F5] p-8 md:p-12 border border-[#E6E6E6]" data-testid="student-application-form">
            {/* Personal Info */}
            <div className="mb-8">
              <h3 className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-6">
                Informations personnelles
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="first_name" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Prénom *
                  </Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Nom *
                  </Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-last-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Téléphone *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="birth_date" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Date de naissance *
                  </Label>
                  <Input
                    id="birth_date"
                    name="birth_date"
                    type="date"
                    value={formData.birth_date}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-birth-date"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="mb-8">
              <h3 className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-6">
                Adresse
              </h3>
              <div className="grid gap-6">
                <div>
                  <Label htmlFor="address" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Adresse *
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-address"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="city" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                      Ville *
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postal_code" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                      Code postal *
                    </Label>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleChange}
                      required
                      className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                      data-testid="input-postal-code"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Education & Motivation */}
            <div className="mb-8">
              <h3 className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-6">
                Parcours et motivation
              </h3>
              <div className="grid gap-6">
                <div>
                  <Label htmlFor="education_level" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Niveau d'études *
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('education_level', value)} value={formData.education_level}>
                    <SelectTrigger className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]" data-testid="select-education">
                      <SelectValue placeholder="Sélectionnez votre niveau" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bac">Baccalauréat</SelectItem>
                      <SelectItem value="bac+1">Bac+1</SelectItem>
                      <SelectItem value="bac+2">Bac+2</SelectItem>
                      <SelectItem value="bac+3">Bac+3 ou plus</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="preferred_start" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Rentrée souhaitée *
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('preferred_start', value)} value={formData.preferred_start}>
                    <SelectTrigger className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]" data-testid="select-start-date">
                      <SelectValue placeholder="Sélectionnez une date" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="septembre-2026">Septembre 2026</SelectItem>
                      <SelectItem value="janvier-2027">Janvier 2027</SelectItem>
                      <SelectItem value="avril-2027">Avril 2027</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="motivation" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Votre motivation *
                  </Label>
                  <Textarea
                    id="motivation"
                    name="motivation"
                    value={formData.motivation}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Décrivez votre projet professionnel et ce qui vous attire dans la vente B2B..."
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="textarea-motivation"
                  />
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none py-6 text-base font-semibold"
              data-testid="submit-application"
            >
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma candidature'}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Students;
